import { useContext, useCallback, useMemo } from 'react';
import {
  SplitPaymentMethod,
  SplitPaymentsContext,
} from '../../context/SplitPaymentsContext/context';
import {
  AddPaymentMethodPayload,
  SplitPaymentForm,
  SplitPaymentOnFinishArgs,
  SplitPaymentsGroupWithCustomer,
} from '../../types/payments';
import useCreateSplitPayment from './useCreateSplitPayment';
import useResetSplitPaymentGroup from './useResetSplitPaymentGroup';
import {
  getCheckoutErrorMessage,
  getPaymentAmountWithoutServiceFee,
  getSplitPaymentsForm,
} from '../../helpers/payments';
import useLoadingModal from '../global/useLoadingModal';
import useCheckoutForm from '../global/useCheckoutForm';
import useProcessTerminalPayment from './useProcessTerminalPayment';
import useCheckoutSocket from './useCheckoutSocket';
import useFinalizeSplitPayments from './useFinalizeSplitPayments';

interface Params {
  isSocketEnabled?: boolean;
  onFinish?: (data: SplitPaymentOnFinishArgs) => void | Promise<void>;
}

function useSplitPayments({ onFinish, isSocketEnabled = false }: Params = {}) {
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { state, dispatch } = useContext(SplitPaymentsContext);
  const { mutateAsync: createSplitPayment, isPending: isCreatingPayment } =
    useCreateSplitPayment();
  const { mutateAsync: resetSplitPayments, isPending: isResetting } =
    useResetSplitPaymentGroup();
  const {
    terminalCheckoutModal,
    setTerminalCheckoutModalToLoading,
    resetTerminalCheckoutModalState,
    setTerminalCheckoutModalToFailed,
  } = useCheckoutForm();

  const {
    mutateAsync: finalizeSplitPayments,
    isSuccess: isFinalized,
    isPending: isFinalizing,
  } = useFinalizeSplitPayments({
    onSuccess: () => {
      if (!state.splitPaymentsGroup?.id) return;
      onFinish?.({
        customerId: state.splitPaymentsGroup.customer.customerId,
        splitPaymentsGroupId: state.splitPaymentsGroup.id,
      });
    },
  });

  const pendingTerminalPayment = useMemo(
    () =>
      terminalCheckoutModal.status === 'loading'
        ? state.paymentMethods.find(p =>
            !p.isPaymentCollected &&
            !p.isError &&
            p.paymentIntentClientSecret &&
            p.paymentMethod === 'TERMINAL'
              ? p.paymentId
              : null,
          )
        : undefined,
    [state.paymentMethods, terminalCheckoutModal.status],
  );

  const { mutateAsync: sendPaymentToTerminal } = useProcessTerminalPayment();

  const updatePaymentMethod = useCallback(
    (paymentId: string, paymentMethod: Partial<SplitPaymentMethod>) => {
      const index = state.paymentMethods.findIndex(
        p => p.paymentId === paymentId,
      );
      if (index < 0) return;
      dispatch({
        type: 'UPDATE_PAYMENT_METHOD',
        payload: { index, paymentMethod },
      });
    },
    [dispatch, state.paymentMethods],
  );

  const onProcessTerminalPayment = useCallback(() => {
    setTerminalCheckoutModalToLoading();
  }, [setTerminalCheckoutModalToLoading]);

  const processTerminalPayment = useCallback(async () => {
    const currentPayment = state.paymentMethods[state.currentIndex];
    if (
      !currentPayment ||
      currentPayment.isPaymentCollected ||
      currentPayment.paymentMethod !== 'TERMINAL'
    )
      return;
    await sendPaymentToTerminal(currentPayment.paymentId);
    onProcessTerminalPayment();
    updatePaymentMethod(currentPayment.paymentId, { isError: false });
  }, [
    onProcessTerminalPayment,
    sendPaymentToTerminal,
    state.currentIndex,
    state.paymentMethods,
    updatePaymentMethod,
  ]);

  const setForm = useCallback(
    (form: SplitPaymentForm) => {
      dispatch({ type: 'SET_FORM', payload: form });
    },
    [dispatch],
  );

  const addPaymentMethod = useCallback(
    async (payload: AddPaymentMethodPayload) => {
      if (!state.splitPaymentsGroup) return null;
      try {
        const result = await createSplitPayment({
          splitPaymentsGroupId: state.splitPaymentsGroup?.id,
          payload: {
            paymentMethod: payload.paymentMethod,
            amountCents: Math.round(payload.amount * 100),
          },
        });

        const isTerminal = payload.paymentMethod === 'TERMINAL';

        const amountSubFees = getPaymentAmountWithoutServiceFee(result.payment);
        const newPaymentMethod: SplitPaymentMethod = {
          amount: amountSubFees,
          paymentId: result.payment.id,
          paymentIntentId: result.payment.stripePaymentIntentId || undefined,
          paymentIntentClientSecret:
            result.paymentIntent?.client_secret || undefined,
          paymentMethod: isTerminal ? 'TERMINAL' : result.payment.method,
          isPaymentCollected:
            result.payment.method !== 'CREDIT' &&
            result.payment.method !== 'TERMINAL',
        };

        if (isTerminal) {
          onProcessTerminalPayment();
        }

        dispatch({
          type: 'ADD_PAYMENT_METHOD',
          payload: newPaymentMethod,
        });

        return newPaymentMethod;
      } catch {
        return null;
      }
    },
    [
      state.splitPaymentsGroup,
      createSplitPayment,
      dispatch,
      onProcessTerminalPayment,
    ],
  );

  const reset = useCallback(
    async (isClose?: boolean) => {
      if (!state.splitPaymentsGroup) return;
      try {
        const message = isClose
          ? 'Please wait while we cancel any pending transactions and exit the split checkout.'
          : 'Please wait while we cancel any pending transactions and reset the split payment.';

        if (state.paymentMethods.length) {
          openLoadingModal({
            header: 'Resetting split checkout',
            message,
          });
        }
        await resetSplitPayments({
          splitPaymentsGroupId: state.splitPaymentsGroup.id,
        });
        dispatch({ type: 'RESET' });
        setForm(getSplitPaymentsForm(0, [], 0));
      } catch (error) {
        // Nothing
      } finally {
        closeLoadingModal();
      }
    },
    [
      closeLoadingModal,
      dispatch,
      openLoadingModal,
      resetSplitPayments,
      setForm,
      state.paymentMethods.length,
      state.splitPaymentsGroup,
    ],
  );

  const setSplitPaymentsGroup = useCallback(
    (splitPaymentsGroup: SplitPaymentsGroupWithCustomer | null) => {
      dispatch({
        type: 'SET_SPLIT_PAYMENTS_GROUP',
        payload: splitPaymentsGroup,
      });
    },
    [dispatch],
  );

  const next = useCallback(
    async (newPaymentMethods: SplitPaymentMethod[]) => {
      if (isFinalizing || !state.splitPaymentsGroup) return;

      const totalAmount = newPaymentMethods.reduce(
        (acc, curr) => acc + curr.amount,
        0,
      );
      const isTotalComplete =
        Math.round(totalAmount * 100) ===
        state.splitPaymentsGroup.totalAmountCents;

      if (isTotalComplete) {
        await finalizeSplitPayments({
          splitPaymentsGroupId: state.splitPaymentsGroup?.id,
        });
      } else {
        dispatch({
          type: 'SET_PAYMENT_COLLECTED',
          payload: {
            index: state.currentIndex,
            isPaymentCollected: true,
          },
        });
        dispatch({
          type: 'SET_CURRENT_INDEX',
          payload: state.currentIndex + 1,
        });
        setForm(
          getSplitPaymentsForm(
            state.splitPaymentsGroup?.totalAmountCents || 0,
            newPaymentMethods,
            state.currentIndex + 1,
          ),
        );
      }
    },
    [
      state.splitPaymentsGroup,
      state.currentIndex,
      finalizeSplitPayments,
      dispatch,
      setForm,
      isFinalizing,
    ],
  );

  const setPaymentCollected = useCallback(
    (index: number, isPaymentCollected: boolean) => {
      dispatch({
        type: 'SET_PAYMENT_COLLECTED',
        payload: {
          index,
          isPaymentCollected,
        },
      });
    },
    [dispatch],
  );

  const onCheckoutCapturable = useCallback(
    (paymentId: string) => {
      const paymentIndex = state.paymentMethods.findIndex(
        p => p.paymentId === paymentId,
      );
      if (paymentIndex < 0) return;

      setPaymentCollected(paymentIndex, true);
      if (paymentIndex === state.currentIndex) {
        next(state.paymentMethods);
      }

      if (terminalCheckoutModal.status === 'loading') {
        resetTerminalCheckoutModalState();
      }
    },
    [
      next,
      resetTerminalCheckoutModalState,
      setPaymentCollected,
      state.currentIndex,
      state.paymentMethods,
      terminalCheckoutModal.status,
    ],
  );

  const onCheckoutFailed = useCallback(
    ({
      paymentId,
      error,
      updateModal = true,
    }: {
      paymentId: string;
      error: string;
      updateModal?: boolean;
    }) => {
      if (!paymentId || paymentId !== pendingTerminalPayment?.paymentId) return;
      if (updateModal) setTerminalCheckoutModalToFailed(error);
      updatePaymentMethod(paymentId, { isError: true });
    },
    [
      pendingTerminalPayment,
      setTerminalCheckoutModalToFailed,
      updatePaymentMethod,
    ],
  );

  useCheckoutSocket({
    enabled: isSocketEnabled,
    onCheckoutCapturable: ({ paymentId }) => {
      if (!paymentId) return;

      onCheckoutCapturable(paymentId);
    },
    onCheckoutFailed: data => {
      const { paymentId } = data;
      const tempFailureMessage = getCheckoutErrorMessage(data);
      onCheckoutFailed({ paymentId, error: tempFailureMessage });
    },
  });

  return {
    splitPaymentsGroup: state.splitPaymentsGroup,
    currentIndex: state.currentIndex,
    addPaymentMethod,
    reset,
    paymentMethods: state.paymentMethods,
    setSplitPaymentsGroup,
    next,
    isResetting,
    isCreatingPayment,
    setPaymentCollected,
    processTerminalPayment,
    pendingTerminalPayment,
    onCheckoutCapturable,
    onCheckoutFailed,
    form: state.form,
    setForm,
    isFinalized,
    isFinalizing,
  };
}

export default useSplitPayments;
